import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Brain, Zap, ChevronRight, RefreshCw, CheckCircle, Clock, AlertCircle,
  Users, Search, Layers, Sparkles, TrendingUp, ChevronDown, ChevronUp,
  FlaskConical, Columns, X, Globe,
} from 'lucide-react';
import DiscoveryChat from '../components/DiscoveryChat';
import { RubiksCube } from '../components/RubiksCube';
import { useBrainstorm } from '../context/BrainstormContext';
import { FileAttachments } from '../components/FileAttachments';
import ModelSelector from '../components/ModelSelector';
import SnapshotBadge from '../components/SnapshotBadge';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Mode definitions ────────────────────────────────────────────────────────────
const MODES = [
  {
    id: 'classic',
    label: 'Classic Council',
    icon: Users,
    color: '#7c5efa',
    badge: 'FAST',
    duration: '~5 min',
    models: 'GPT-4o · Gemini 3.1 · Perplexity → Groq · Mistral · Command R+',
    description: '6 rotating AI models deliberate across 4 escalating rounds — different LLMs each round. Claude Sonnet synthesizes each round into a final blueprint.',
  },
  {
    id: 'smart_council',
    label: 'Smart Council',
    icon: Search,
    color: '#00d4b8',
    badge: 'RESEARCHED',
    duration: '~10 min',
    models: 'Rotating 6-model council + Perplexity Research',
    description: 'After Round 2, Perplexity validates ideas with real-time web research. Each round brings fresh AI perspectives. Claude uses validated insights for the final output.',
  },
  {
    id: 'dual_council',
    label: 'Dual Council Auto',
    icon: Zap,
    color: '#f97316',
    badge: 'DEEP',
    duration: '~20 min',
    models: 'Classic ↔ Smart (unique LLMs per round)',
    description: 'Classic output feeds Smart Council — each of 4 rounds uses a completely different LLM combination. Dynamic AI personas per model. Optional Autoresearch finale.',
    highlight: true,
  },
];

// ── Visual styles ───────────────────────────────────────────────────────────────
const ROUND_COLORS = [
  { bg: 'rgba(124,94,250,0.1)', border: 'rgba(124,94,250,0.3)', text: '#a68eff', dot: '#7c5efa' },
  { bg: 'rgba(249,115,22,0.08)', border: 'rgba(249,115,22,0.3)', text: '#fb923c', dot: '#f97316' },
  { bg: 'rgba(0,212,184,0.08)', border: 'rgba(0,212,184,0.3)', text: '#00d4b8', dot: '#00d4b8' },
  { bg: 'rgba(240,194,127,0.1)', border: 'rgba(240,194,127,0.3)', text: '#f0c27f', dot: '#f0c27f' },
];

const DUAL_BADGES = {
  classic: { label: 'CLASSIC', bg: 'rgba(124,94,250,0.15)', color: '#a68eff', border: 'rgba(124,94,250,0.3)' },
  smart: { label: 'SMART', bg: 'rgba(249,115,22,0.15)', color: '#fb923c', border: 'rgba(249,115,22,0.3)' },
};

const MODEL_COLORS = {
  'GPT-4o': '#00d4b8',
  'Gemini 3.1 Pro': '#4285f4',
  'Gemini 2.5 Pro': '#4285f4',
  'Perplexity Sonar Pro': '#6366f1',
  'Groq Llama 3.3-70B': '#f97316',
  'Mistral Large 2': '#ff7a94',
  'Command R+': '#3dffa0',
};

const FAMILY_LABELS = {
  openai: 'GPT-4o',
  google: 'Gemini 3.1 Pro',
  groq: 'Groq Llama 3.3-70B',
  perplexity: 'Perplexity Sonar Pro',
  mistral: 'Mistral Large 2',
  cohere: 'Command R+',
};

// ── Persona Panel ───────────────────────────────────────────────────────────────
function PersonasPanel({ personas, families }) {
  const [open, setOpen] = useState(false);
  if (!personas || Object.keys(personas).length === 0) return null;
  return (
    <div className="mb-3 rounded-xl overflow-hidden" style={{ border: '1px solid rgba(249,115,22,0.25)', background: 'rgba(249,115,22,0.04)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
      >
        <Sparkles size={12} style={{ color: '#fb923c' }} />
        <span className="text-[10px] font-semibold" style={{ color: '#fb923c' }}>
          Dynamic Personas Generated — {Object.keys(personas).length} specialized experts
        </span>
        <span className="ml-auto">{open ? <ChevronUp size={12} style={{ color: '#fb923c' }} /> : <ChevronDown size={12} style={{ color: '#fb923c' }} />}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {(families || Object.keys(personas)).map(f => (
            <div key={f} className="rounded-lg p-2.5" style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)' }}>
              <div className="text-[10px] font-bold mb-0.5" style={{ color: MODEL_COLORS[FAMILY_LABELS[f]] || '#fb923c' }}>
                {FAMILY_LABELS[f] || f}
              </div>
              <div className="text-[10px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {personas[f] || '—'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Autoresearch Event Card (smart_council inline) ──────────────────────────────
function AutoresearchCard({ event }) {
  if (!event) return null;
  if (event.type === 'start') {
    return (
      <div className="rounded-xl p-3 flex items-center gap-2 mb-3" style={{ background: 'rgba(0,212,184,0.06)', border: '1px solid rgba(0,212,184,0.2)' }}>
        <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#00d4b8' }} />
        <span className="text-xs" style={{ color: '#00d4b8' }}>Autoresearcher running — Perplexity + Claude validation...</span>
      </div>
    );
  }
  if (event.type === 'complete') {
    return (
      <div className="rounded-xl p-3 mb-3" style={{ background: 'rgba(0,212,184,0.06)', border: '1px solid rgba(0,212,184,0.2)' }}>
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle size={12} style={{ color: '#00d4b8' }} />
          <span className="text-[10px] font-semibold" style={{ color: '#00d4b8' }}>
            Autoresearch Complete — {(event.confidence * 100).toFixed(0)}% confidence
          </span>
        </div>
        <p className="text-xs leading-relaxed mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>{event.synthesis}</p>
        {event.recommendations?.length > 0 && (
          <div className="space-y-1">
            {event.recommendations.slice(0, 3).map((r, i) => (
              <div key={i} className="text-[10px]" style={{ color: 'rgba(0,212,184,0.7)' }}>→ {r}</div>
            ))}
          </div>
        )}
      </div>
    );
  }
  return null;
}

// ── Round Card ─────────────────────────────────────────────────────────────────
function RoundCard({ round, index, isDualCouncil, autoresearchEvent }) {
  const [expanded, setExpanded] = useState(false);
  const colors = ROUND_COLORS[index % ROUND_COLORS.length];
  const isComplete = round.status === 'complete';
  const isRunning = round.status === 'running';
  const isActive = isRunning || isComplete;
  const dualBadge = isDualCouncil && DUAL_BADGES[round.roundType || round.type];

  useEffect(() => {
    if (isComplete) setExpanded(true);
  }, [isComplete]);

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={{
        background: isActive ? colors.bg : 'rgba(255,255,255,0.02)',
        border: `1px solid ${isActive ? colors.border : 'var(--llmc-border)'}`,
        animation: isActive ? 'fade-in 0.4s ease' : 'none',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
        onClick={() => isComplete && setExpanded(e => !e)}
      >
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0"
          style={{ background: isActive ? colors.dot : 'rgba(255,255,255,0.06)', color: isActive ? '#fff' : 'var(--llmc-text-muted)' }}
        >
          {isComplete ? <CheckCircle size={14} /> : isRunning ? (
            <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: colors.dot }} />
          ) : round.roundNumber}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-white truncate">Round {round.roundNumber}: {round.label}</span>
            {isDualCouncil && dualBadge && (
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded flex-shrink-0"
                style={{ background: dualBadge.bg, color: dualBadge.color, border: `1px solid ${dualBadge.border}` }}>
                {dualBadge.label}
              </span>
            )}
          </div>
          <div className="text-[10px] truncate" style={{ color: 'var(--llmc-text-muted)' }}>
            {round.models?.join(' · ')}
          </div>
        </div>

        {isRunning && (
          <div className="text-[10px] font-semibold px-2 py-0.5 rounded-full animate-pulse flex-shrink-0"
            style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }}>
            RUNNING
          </div>
        )}
        {isComplete && (
          <ChevronRight size={14} className="transition-transform flex-shrink-0"
            style={{ color: 'var(--llmc-text-muted)', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }} />
        )}
      </div>

      {/* Content (when expanded or running) */}
      {isActive && expanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Autoresearch event (smart_council) */}
          {autoresearchEvent && <AutoresearchCard event={autoresearchEvent} />}

          {/* Dynamic Personas (dual council smart rounds) */}
          {isDualCouncil && round.personas && Object.keys(round.personas).length > 0 && (
            <PersonasPanel personas={round.personas} families={null} />
          )}

          {/* Council Opinions */}
          {round.opinions?.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                style={{ color: 'var(--llmc-text-muted)' }}>Council Opinions</div>
              <div className="space-y-2">
                {round.opinions.map((op, i) => (
                  <div key={i} className="rounded-lg p-3"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--llmc-border)' }}>
                    <div className="text-[10px] font-bold mb-1" style={{ color: MODEL_COLORS[op.model] || colors.text }}>
                      {op.model}
                    </div>
                    <div className="text-xs leading-relaxed" style={{ color: 'var(--llmc-text-muted)' }}>
                      {op.content?.startsWith('[unavailable') ? (
                        <span className="text-red-400 italic">{op.content}</span>
                      ) : op.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Synthesis */}
          {isComplete && round.synthesis && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5"
                style={{ color: colors.text }}>
                <Zap size={10} /> Claude Judge Synthesis
              </div>
              <div className="rounded-xl p-4 text-sm leading-relaxed whitespace-pre-wrap"
                style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: '#e8e8f0' }}>
                {round.synthesis}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Autoresearch CTA (Dual Council final step) ─────────────────────────────────
function AutoresearchCTA({ sessionId }) {
  const [state, setState] = useState('idle'); // idle | loading | done | error
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const run = async () => {
    setState('loading');
    setErr('');
    try {
      const { data } = await axios.post(`${API}/api/brainstorm/autoresearch/${sessionId}`);
      setResult(data);
      setState('done');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Autoresearch failed');
      setState('error');
    }
  };

  if (state === 'done' && result) {
    return (
      <div className="rounded-2xl p-5 space-y-3" style={{ background: 'rgba(124,94,250,0.06)', border: '1px solid rgba(124,94,250,0.25)' }}>
        <div className="flex items-center gap-2">
          <CheckCircle size={16} style={{ color: '#a68eff' }} />
          <span className="font-bold text-sm text-white">Autoresearch Enhanced</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full ml-auto font-semibold"
            style={{ background: 'rgba(124,94,250,0.2)', color: '#a68eff' }}>
            {(result.confidence * 100).toFixed(0)}% confidence
          </span>
        </div>
        {result.synthesis && (
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>{result.synthesis}</p>
        )}
        {result.recommendations?.length > 0 && (
          <div>
            <div className="text-[10px] font-semibold mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>ENHANCEMENTS APPLIED</div>
            {result.recommendations.map((r, i) => (
              <div key={i} className="flex gap-2 text-xs mb-1">
                <span style={{ color: '#a68eff' }}>→</span>
                <span style={{ color: 'rgba(255,255,255,0.55)' }}>{r}</span>
              </div>
            ))}
          </div>
        )}
        {result.enhanced_output && (
          <div className="rounded-xl p-4" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
            <div className="text-[10px] font-semibold mb-2 flex items-center gap-1.5" style={{ color: '#a68eff' }}>
              <Sparkles size={10} /> Final Enhanced Output
            </div>
            <div className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: '#e8e8f0' }}>
              {result.enhanced_output}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl p-4" style={{ background: 'rgba(124,94,250,0.05)', border: '1px dashed rgba(124,94,250,0.3)' }}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(124,94,250,0.15)', border: '1px solid rgba(124,94,250,0.3)' }}>
          <Sparkles size={16} style={{ color: '#a68eff' }} />
        </div>
        <div className="flex-1">
          <div className="font-bold text-sm text-white mb-0.5">Optional: Enhance with Autoresearch</div>
          <div className="text-xs mb-3" style={{ color: 'var(--llmc-text-muted)' }}>
            Perplexity Sonar Pro validates your blueprint with real-time research. Claude applies all validated recommendations for the definitive final output.
          </div>
          {err && <div className="text-xs text-red-400 mb-2">{err}</div>}
          <button
            onClick={run}
            disabled={state === 'loading'}
            data-testid="brainstorm-autoresearch-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all hover:opacity-90 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #7c5efa, #a68eff)', color: '#fff' }}
          >
            {state === 'loading' ? (
              <><div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />Researching with Perplexity...</>
            ) : (
              <><Sparkles size={14} />Run Autoresearch</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Session Storage Helpers ────────────────────────────────────────────────────
const STORAGE_KEY = 'brainstorm_sessions';
function saveSessionHistory(session) {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    stored.unshift(session);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored.slice(0, 5)));
  } catch {}
}
function loadSessionHistory() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
}

// ── Compare Panel ──────────────────────────────────────────────────────────────
function ComparePanel({ sessions, onClose }) {
  const [a, b] = sessions.slice(0, 2);
  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'rgba(5,5,15,0.97)', backdropFilter: 'blur(20px)' }}>
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
        <div className="flex items-center gap-2">
          <Columns size={16} style={{ color: '#a68eff' }} />
          <span className="font-black text-white text-sm">Side-by-Side Comparison</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(124,94,250,0.15)', color: '#a68eff' }}>Last 2 sessions</span>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg hover:opacity-80" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--llmc-text-muted)' }}>
          <X size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-hidden flex gap-0">
        {[a, b].filter(Boolean).map((sess, i) => (
          <div key={i} className="flex-1 overflow-y-auto p-5 border-r" style={{ borderColor: 'var(--llmc-border)' }}>
            <div className="mb-4 pb-3" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
              <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: i === 0 ? '#a68eff' : '#00d4b8' }}>
                {i === 0 ? 'Session A (Latest)' : 'Session B (Previous)'}
              </div>
              <div className="text-sm font-semibold text-white mb-0.5">{sess.idea}</div>
              <div className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>
                {sess.mode} · {new Date(sess.timestamp).toLocaleDateString()}
              </div>
            </div>
            <div className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: 'rgba(255,255,255,0.75)' }}>
              {sess.synthesis || 'No synthesis available'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Mode Card ──────────────────────────────────────────────────────────────────
function ModeCard({ mode, selected, onSelect }) {
  const Icon = mode.icon;
  return (
    <button
      onClick={() => onSelect(mode.id)}
      data-testid={`mode-${mode.id}`}
      className="flex flex-col gap-2 p-4 rounded-2xl text-left transition-all duration-200 hover:opacity-90"
      style={{
        background: selected ? `${mode.color}14` : 'rgba(255,255,255,0.02)',
        border: `1.5px solid ${selected ? mode.color + '60' : 'var(--llmc-border)'}`,
        flex: 1,
        boxShadow: selected ? `0 0 20px ${mode.color}22` : 'none',
        outline: mode.highlight && !selected ? `1px dashed ${mode.color}40` : 'none',
      }}
    >
      <div className="flex items-center justify-between">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center"
          style={{ background: `${mode.color}20`, border: `1px solid ${mode.color}40` }}>
          <Icon size={15} style={{ color: mode.color }} />
        </div>
        <div className="flex items-center gap-1.5">
          {mode.highlight && (
            <span className="text-[8px] font-black px-1.5 py-0.5 rounded"
              style={{ background: `${mode.color}25`, color: mode.color }}>BEST</span>
          )}
          <span className="text-[9px] font-black px-1.5 py-0.5 rounded"
            style={{ background: `${mode.color}18`, color: mode.color, border: `1px solid ${mode.color}30` }}>
            {mode.badge}
          </span>
        </div>
      </div>

      <div>
        <div className="font-black text-sm text-white mb-0.5" style={{ letterSpacing: '-0.01em' }}>{mode.label}</div>
        <div className="text-[10px] leading-relaxed" style={{ color: 'var(--llmc-text-muted)' }}>{mode.description}</div>
      </div>

      <div className="flex items-center gap-2 mt-auto pt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Clock size={10} style={{ color: 'rgba(255,255,255,0.2)' }} />
        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.25)' }}>{mode.duration}</span>
        <span className="text-[10px] truncate ml-auto" style={{ color: selected ? mode.color : 'rgba(255,255,255,0.2)' }}>
          {mode.models}
        </span>
      </div>
    </button>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function BrainstormLab() {
  const [idea, setIdea] = useState(() => {
    // Support prefill from AutoStrategy "Brainstorm with Council" button
    const prefill = localStorage.getItem('ideaworks_prefill_idea');
    if (prefill) { localStorage.removeItem('ideaworks_prefill_idea'); return prefill; }
    return '';
  });
  const [prefillContext] = useState(() => {
    const ctx = localStorage.getItem('ideaworks_prefill_context');
    if (ctx) { localStorage.removeItem('ideaworks_prefill_context'); try { return JSON.parse(ctx); } catch { return null; } }
    return null;
  });
  const [mode, setMode] = useState('classic');
  const [councilSize, setCouncilSize] = useState(3);
  const [sessionId, setSessionId] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [status, setStatus] = useState('idle');       // idle | discovery | running | complete | error
  const [error, setError] = useState(null);
  const [sessionSnapshot, setSessionSnapshot] = useState(null);
  const [autoresearchAvailable, setAutoresearchAvailable] = useState(false);
  const [autoresearchEvents, setAutoresearchEvents] = useState({ type: null });
  const [personasGenerating, setPersonasGenerating] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [sessionHistory, setSessionHistory] = useState(() => loadSessionHistory());
  const [finalSynthesis, setFinalSynthesis] = useState('');
  const [discoveryContext, setDiscoveryContext] = useState(null);
  // Parallel Classic vs Smart comparison mode
  const [parallelMode, setParallelMode] = useState(false);
  const [parallelResults, setParallelResults] = useState({ classic: null, smart: null, loading: '' });
  const [judgeModel, setJudgeModel] = useState(null); // null = default (Claude Sonnet 4.6)
  const esRef = useRef(null);
  const bottomRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const activeMode = MODES.find(m => m.id === mode) || MODES[0];
  const isDualCouncil = mode === 'dual_council';
  const { startSession, updateRound, completeSession, clearSession } = useBrainstorm();

  // Accept idea pre-filled from Trending Topics / other pages
  useEffect(() => {
    if (location.state?.idea) {
      setIdea(location.state.idea);
    }
  }, [location.state]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [rounds, autoresearchAvailable]);

  // ── Parallel Classic vs Smart (sequential under the hood) ───────────────────
  const runParallelCompare = useCallback(async () => {
    const theIdea = discoveryContext || idea;
    if (!theIdea.trim()) return;
    setParallelResults({ classic: null, smart: null, loading: 'classic' });

    const runMode = async (m) => {
      const { data } = await axios.post(`${API}/api/brainstorm/start`, { idea: theIdea, mode: m, council_size: 3 });
      const sid = data.sessionId;
      // Poll for completion
      for (let attempt = 0; attempt < 120; attempt++) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const { data: sess } = await axios.get(`${API}/api/brainstorm/session/${sid}`);
          if (sess.session?.status === 'complete') {
            const rounds = sess.session?.rounds || [];
            const lastSynthesis = [...rounds].reverse().find(r => r.synthesis)?.synthesis || '';
            return { synthesis: lastSynthesis, rounds, sessionId: sid };
          }
        } catch {}
      }
      throw new Error('Timeout');
    };

    try {
      const classicResult = await runMode('classic');
      setParallelResults(p => ({ ...p, classic: classicResult, loading: 'smart' }));
      const smartResult = await runMode('smart_council');
      setParallelResults({ classic: classicResult, smart: smartResult, loading: '' });
    } catch (e) {
      setParallelResults(p => ({ ...p, loading: '' }));
      alert('Parallel comparison failed. Try again.');
    }
  }, [idea, discoveryContext]);

  const startBrainstorm = useCallback(async (contextToUse) => {
    const theIdea = contextToUse || discoveryContext || idea;
    if (!theIdea.trim() || status === 'running') return;
    setError(null);
    setRounds([]);
    setAutoresearchAvailable(false);
    setAutoresearchEvents({ type: null });
    setPersonasGenerating(null);
    setFinalSynthesis('');
    setStatus('connecting');

    try {
      const { data } = await axios.post(`${API}/api/brainstorm/start`, {
        idea: theIdea,
        mode,
        council_size: councilSize,
        attachments: attachments.map(a => ({ name: a.name, type: a.type, content: a.content })),
        ...(judgeModel ? { judge_model: judgeModel } : {}),
      });
      const sid = data.sessionId;
      setSessionId(sid);
      // Store snapshot from start response
      if (data.snapshot) {
        setSessionSnapshot(data.snapshot);
      }
      setRounds(
        data.rounds.map(r => ({
          roundNumber: r.roundNumber,
          label: r.label,
          roundType: r.roundType || (isDualCouncil ? 'classic' : mode),
          models: r.models,
          opinions: [],
          synthesis: '',
          status: 'pending',
          personas: {},
        }))
      );
      setStatus('running');
      startSession({ sessionId: sid, idea, mode: activeMode.label, modeColor: activeMode.color, totalRounds: data.rounds.length });

      const es = new EventSource(`${API}/api/brainstorm/stream/${sid}`);
      esRef.current = es;

      es.addEventListener('session', e => {
        const d = JSON.parse(e.data);
        if (d.rounds) {
          setRounds(d.rounds.map(r => ({ ...r, roundType: r.roundType || 'classic', opinions: [], synthesis: '', personas: {} })));
        }
      });

      es.addEventListener('round:start', e => {
        const d = JSON.parse(e.data);
        setRounds(prev => prev.map(r =>
          r.roundNumber === d.round
            ? { ...r, status: 'running', roundType: d.type || r.roundType, models: d.models || r.models }
            : r
        ));
      });

      es.addEventListener('personas:generating', e => {
        const d = JSON.parse(e.data);
        setPersonasGenerating(d.round);
      });

      es.addEventListener('personas:generated', e => {
        const d = JSON.parse(e.data);
        setPersonasGenerating(null);
        setRounds(prev => prev.map(r =>
          r.roundNumber === d.round ? { ...r, personas: d.personas || {} } : r
        ));
      });

      es.addEventListener('round:opinions', e => {
        const d = JSON.parse(e.data);
        setRounds(prev => prev.map(r =>
          r.roundNumber === d.round ? { ...r, opinions: d.opinions } : r
        ));
      });

      es.addEventListener('round:complete', e => {
        const d = JSON.parse(e.data);
        setRounds(prev => prev.map(r =>
          r.roundNumber === d.round
            ? { ...r, synthesis: d.synthesis, status: 'complete', personas: d.personas || r.personas }
            : r
        ));
        // Track final synthesis (last round)
        setFinalSynthesis(d.synthesis || '');
        updateRound(d.round);
      });

      es.addEventListener('autoresearch:start', () => {
        setAutoresearchEvents({ type: 'start' });
      });

      es.addEventListener('autoresearch:complete', e => {
        const d = JSON.parse(e.data);
        setAutoresearchEvents({ type: 'complete', ...d });
      });

      es.addEventListener('autoresearch:error', e => {
        const d = JSON.parse(e.data);
        setAutoresearchEvents({ type: 'error', error: d.error });
      });

      es.addEventListener('complete', e => {
        const d = JSON.parse(e.data);
        setStatus('complete');
        if (d.autoresearch_available) setAutoresearchAvailable(true);
        completeSession();
        es.close();
      });

      // Custom backend error event (has data)
      es.addEventListener('error', e => {
        if (!e.data) return; // Native SSE connection event — handled by onerror
        try {
          const d = JSON.parse(e.data);
          setError(d.error || 'Stream error');
        } catch {
          setError('Stream error');
        }
        setStatus('error');
        es.close();
      });

      // Native SSE connection error/close
      es.onerror = () => {
        setStatus(prev => prev === 'running' ? 'complete' : prev);
        es.close();
      };

    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to start');
      setStatus('error');
    }
  }, [idea, mode, councilSize, discoveryContext, status, attachments]);

  const reset = () => {
    if (esRef.current) esRef.current.close();
    setSessionId(null);
    setRounds([]);
    setStatus('idle');
    setError(null);
    setIdea('');
    setAutoresearchAvailable(false);
    setAutoresearchEvents({ type: null });
    setPersonasGenerating(null);
    setFinalSynthesis('');
    setAttachments([]);
    clearSession();
    setDiscoveryContext(null);
  };

  // Save completed session to localStorage for comparison
  useEffect(() => {
    if (status === 'complete' && finalSynthesis && sessionId) {
      const session = { id: sessionId, idea, mode: activeMode.label, synthesis: finalSynthesis, timestamp: Date.now() };
      saveSessionHistory(session);
      setSessionHistory(loadSessionHistory());
    }
  }, [status, finalSynthesis, sessionId]);

  const sendToResearch = () => {
    // Build a concise research topic from the brainstorm output
    const researchTopic = `${idea.slice(0, 200)}${finalSynthesis ? ` — Key insights: ${finalSynthesis.slice(0, 400)}` : ''}`;
    navigate('/research', { state: { topic: researchTopic, fromBrainstorm: true } });
  };

  const EXAMPLES = [
    'A product better than Claude Code for developers',
    'AI-powered personal finance coach for Gen Z',
    'Decentralized social media that pays creators',
    'SaaS for cybersecurity awareness training',
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: 'var(--llmc-bg)' }}>
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4" style={{ borderBottom: '1px solid var(--llmc-border)', background: 'rgba(10,10,20,0.95)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #ff5370, #7c5efa)' }}>
              <Brain size={20} className="text-white" />
            </div>
            <div>
              <div className="font-black text-white text-sm" style={{ letterSpacing: '-0.01em' }}>Brainstorm Lab</div>
              <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
                Classic · Smart · Dual Council Auto — powered by Claude Judge
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {status !== 'idle' && (
              <div className="text-[10px] font-bold px-2.5 py-1 rounded-full"
                style={{ background: `${activeMode.color}18`, border: `1px solid ${activeMode.color}40`, color: activeMode.color }}>
                {activeMode.label}
              </div>
            )}
            {status !== 'idle' && (
              <button onClick={reset} data-testid="brainstorm-reset-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--llmc-border)', color: 'var(--llmc-text-muted)' }}>
                <RefreshCw size={12} /> Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">

        {/* Idle: mode selector + idea input (also show in 'discovery' status so DiscoveryChat renders) */}
        {(status === 'idle' || status === 'discovery') && (
          <div className="max-w-3xl mx-auto space-y-5" style={{ animation: 'fade-in 0.4s ease' }}>
            <div className="text-center pt-6 pb-2">
              <div className="text-3xl font-black text-white mb-2" style={{ letterSpacing: '-0.02em' }}>What do you want to brainstorm?</div>
              <div className="text-sm" style={{ color: 'var(--llmc-text-muted)' }}>Choose your brainstorm mode, then describe your idea.</div>
            </div>

            {/* Mode selector */}
            <div className="flex gap-3">
              {MODES.map(m => (
                <ModeCard key={m.id} mode={m} selected={mode === m.id} onSelect={(id) => { setMode(id); setParallelMode(false); }} />
              ))}
              {/* Parallel Compare toggle */}
              <button
                onClick={() => setParallelMode(v => !v)}
                data-testid="parallel-mode-toggle"
                className="flex-1 rounded-2xl p-4 text-left transition-all border-2"
                style={{
                  background: parallelMode ? 'rgba(249,115,22,0.08)' : 'rgba(255,255,255,0.02)',
                  borderColor: parallelMode ? '#f97316' : 'var(--llmc-border)',
                  opacity: parallelMode ? 1 : 0.7,
                }}>
                <div className="flex items-center gap-2 mb-1">
                  <Columns size={14} style={{ color: '#f97316' }} />
                  <span className="text-xs font-black text-white">Side-by-Side</span>
                  <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full ml-auto" style={{ background: 'rgba(249,115,22,0.15)', color: '#f97316' }}>VS</span>
                </div>
                <div className="text-[10px] leading-snug" style={{ color: 'var(--llmc-text-muted)' }}>Classic vs Smart Council — compare both outputs in split view</div>
              </button>
            </div>

            {/* Parallel comparison results view */}
            {parallelMode && (parallelResults.classic || parallelResults.loading) && (
              <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(249,115,22,0.3)' }}>
                <div className="flex items-center gap-2 px-4 py-3" style={{ background: 'rgba(249,115,22,0.08)', borderBottom: '1px solid rgba(249,115,22,0.2)' }}>
                  <Columns size={14} style={{ color: '#f97316' }} />
                  <span className="text-xs font-bold text-white">Classic vs Smart — Comparison View</span>
                  {parallelResults.loading && <span className="text-[10px] ml-auto animate-pulse" style={{ color: '#f97316' }}>Running {parallelResults.loading} council...</span>}
                  {!parallelResults.loading && parallelResults.smart && <span className="text-[10px] ml-auto" style={{ color: '#3dffa0' }}>Both complete — compare below</span>}
                </div>
                <div className="grid grid-cols-2 divide-x" style={{ borderColor: 'rgba(249,115,22,0.2)' }}>
                  {['classic', 'smart'].map(side => (
                    <div key={side} className="p-4">
                      <div className="text-[10px] font-black uppercase mb-2" style={{ color: side === 'classic' ? '#a68eff' : '#00d4b8' }}>
                        {side === 'classic' ? 'Classic Council' : 'Smart Council'}
                      </div>
                      {parallelResults.loading === side ? (
                        <div className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                          <RefreshCw size={12} className="animate-spin" /> Thinking...
                        </div>
                      ) : parallelResults[side] ? (
                        <div className="text-xs leading-relaxed" style={{ color: '#e8e8f0', maxHeight: 300, overflowY: 'auto' }}>
                          {parallelResults[side].synthesis || 'Processing...'}
                        </div>
                      ) : (
                        <div className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>Waiting...</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Council Size Selector */}
            <div className="rounded-2xl p-4" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
              <div className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--llmc-text-muted)' }}>
                Council Size — how many AI members deliberate?
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { value: 3, label: '3-Member', tag: 'Quick', color: '#3dffa0', desc: '~2 min' },
                  { value: 5, label: '5-Member', tag: 'Standard', color: '#60a5fa', desc: '~5 min' },
                  { value: 7, label: '7-Member', tag: 'Deep', color: '#a68eff', desc: '~8 min' },
                  { value: 9, label: '9-Member', tag: 'Elite', color: '#f0c27f', desc: '~15 min' },
                ].map(cs => (
                  <button
                    key={cs.value}
                    onClick={() => setCouncilSize(cs.value)}
                    data-testid={`council-size-${cs.value}`}
                    className="flex flex-col items-center gap-1 px-3 py-3 rounded-xl transition-all hover:opacity-90"
                    style={{
                      background: councilSize === cs.value ? `${cs.color}12` : 'rgba(255,255,255,0.02)',
                      border: `1.5px solid ${councilSize === cs.value ? cs.color : 'var(--llmc-border)'}`,
                    }}
                  >
                    <div className="text-xs font-black text-white">{cs.label}</div>
                    <div className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                      style={{ background: `${cs.color}15`, color: cs.color }}>
                      {cs.tag}
                    </div>
                    <div className="text-[9px]" style={{ color: 'var(--llmc-text-muted)' }}>{cs.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Idea input */}
            <div className="rounded-2xl p-5" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
              <textarea
                value={idea}
                onChange={e => setIdea(e.target.value)}
                data-testid="brainstorm-idea-input"
                placeholder={
                  isDualCouncil
                    ? "e.g. A product better than Claude Code — what features should we build?"
                    : "Describe your idea, product concept, business strategy, or any challenge to explore..."
                }
                className="w-full bg-transparent resize-none text-sm outline-none leading-relaxed"
                style={{ color: 'var(--llmc-text)', minHeight: 100, maxHeight: 200 }}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { if (idea.trim()) setStatus('discovery'); } }}
              />
              {/* Attachments */}
              {attachments.length > 0 && (
                <div className="mt-2 pb-2" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
                  <FileAttachments attachments={attachments} onChange={setAttachments} />
                </div>
              )}

              {/* Discovery Mode panel — shown when status='discovery' */}
              {status === 'discovery' && idea.trim() && (
                <div className="mt-3">
                  <DiscoveryChat
                    context={idea}
                    sessionType="brainstorm"
                    accentColor="#a68eff"
                    accentBg="rgba(166,142,255,0.07)"
                    onComplete={({ enrichedContext, summary }) => {
                      setDiscoveryContext(enrichedContext);
                      startBrainstorm(enrichedContext);
                    }}
                    onSkip={(ctx) => {
                      setStatus('idle');
                      startBrainstorm(ctx);
                    }}
                  />
                </div>
              )}

              {status === 'idle' && (
                <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid var(--llmc-border)' }}>
                  <div className="flex items-center gap-2">
                    <FileAttachments attachments={attachments} onChange={setAttachments} />
                  </div>
                {parallelMode ? (
                  <button
                    onClick={runParallelCompare}
                    disabled={!idea.trim() || !!parallelResults.loading}
                    data-testid="parallel-compare-start-btn"
                    className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold transition-all hover:scale-105 disabled:opacity-40"
                    style={{ background: 'linear-gradient(135deg, #f97316, #fb923c)', color: '#fff' }}>
                    {parallelResults.loading ? <RefreshCw size={15} className="animate-spin" /> : <Columns size={15} />}
                    {parallelResults.loading ? `Running ${parallelResults.loading}...` : 'Compare Classic vs Smart'}
                  </button>
                ) : (
                <button
                  onClick={() => { if (idea.trim()) setStatus('discovery'); }}
                  disabled={!idea.trim()}
                  data-testid="brainstorm-start-btn"
                  className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold transition-all hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{ background: `linear-gradient(135deg, ${activeMode.color}, ${activeMode.color}bb)`, color: '#fff' }}>
                  <activeMode.icon size={15} /> Start Discovery
                </button>
                )}
                <ModelSelector lab="brainstorm" onChange={setJudgeModel} />
              </div>
              )}
            </div>

            {/* Examples */}
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--llmc-text-muted)' }}>Try an example</div>
              <div className="grid grid-cols-2 gap-2">
                {EXAMPLES.map((ex, i) => (
                  <button key={i} onClick={() => setIdea(ex)} data-testid={`brainstorm-example-${i}`}
                    className="text-left px-3 py-2.5 rounded-xl text-xs transition-all hover:opacity-80"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--llmc-border)', color: 'var(--llmc-text-muted)' }}>
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Running / Complete */}
        {status !== 'idle' && status !== 'discovery' && (
          <div className="max-w-2xl mx-auto space-y-3">
            {/* Topic + mode banner */}
            <div className="rounded-xl p-4" style={{ background: `${activeMode.color}09`, border: `1px solid ${activeMode.color}35` }}>
              <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: activeMode.color + 'bb' }}>
                {activeMode.label}
              </div>
              <div className="text-sm font-semibold text-white">{idea}</div>
            </div>

            {/* Status bar */}
            <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl"
              style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
              {status === 'running' ? (
                <>
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: activeMode.color }} />
                  <span className="text-xs font-medium" style={{ color: 'var(--llmc-text-muted)' }}>
                    {personasGenerating
                      ? `Generating dynamic personas for Round ${personasGenerating}...`
                      : `Council deliberating... (${rounds.filter(r => r.status === 'complete').length}/${rounds.length} rounds complete)`
                    }
                  </span>
                </>
              ) : status === 'complete' ? (
                <>
                  <CheckCircle size={14} style={{ color: '#00d4b8' }} />
                  <span className="text-xs font-medium" style={{ color: '#00d4b8' }}>
                    {isDualCouncil ? 'Dual Council complete — 4 rounds done' : 'Blueprint complete — all rounds done'}
                  </span>
                </>
              ) : status === 'connecting' ? (
                <>
                  <div className="w-2 h-2 rounded-full animate-ping" style={{ background: activeMode.color }} />
                  <span className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>Connecting to council...</span>
                </>
              ) : (
                <>
                  <AlertCircle size={14} style={{ color: '#ff5370' }} />
                  <span className="text-xs" style={{ color: '#ff5370' }}>{error || 'Error occurred'}</span>
                </>
              )}
            </div>

            {/* Pipeline snapshot (shown when complete) */}
            {status === 'complete' && sessionSnapshot && (
              <div className="mt-1 mb-2">
                <SnapshotBadge snapshot={sessionSnapshot} type="brainstorm" />
              </div>
            )}

            {/* Rubik's Cube animation during processing */}
            {status === 'running' && (
              <div style={{ animation: 'fade-in 0.5s ease' }}>
                <RubiksCube
                  accentColor={activeMode.color}
                  solving={true}
                  size={isDualCouncil ? 120 : 100}
                  roundLabel={
                    personasGenerating
                      ? `Generating personas for Round ${personasGenerating}...`
                      : rounds.find(r => r.status === 'running')?.label || 'Deliberating...'
                  }
                  roundType={rounds.find(r => r.status === 'running')?.roundType || 'classic'}
                  currentRound={rounds.filter(r => r.status === 'complete').length}
                  totalRounds={rounds.length}
                />
              </div>
            )}

            {/* Round cards */}
            {rounds.map((round, i) => (              <RoundCard
                key={round.roundNumber}
                round={round}
                index={i}
                isDualCouncil={isDualCouncil}
                autoresearchEvent={!isDualCouncil && i === 2 ? autoresearchEvents : null}
              />
            ))}

            {/* Dual Council Autoresearch CTA */}
            {isDualCouncil && autoresearchAvailable && sessionId && (
              <AutoresearchCTA sessionId={sessionId} />
            )}

            {/* Post-completion Actions */}
            {status === 'complete' && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2 pt-2">
                  <button
                    onClick={sendToResearch}
                    data-testid="send-to-research-btn"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:scale-105"
                    style={{ background: 'linear-gradient(135deg, rgba(61,255,160,0.15), rgba(0,212,184,0.15))', border: '1px solid rgba(61,255,160,0.4)', color: '#3dffa0' }}
                  >
                    <FlaskConical size={13} /> Deep Research This
                  </button>
                  {sessionHistory.length >= 2 && (
                    <button
                      onClick={() => setShowCompare(true)}
                      data-testid="compare-sessions-btn"
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:scale-105"
                      style={{ background: 'rgba(124,94,250,0.12)', border: '1px solid rgba(124,94,250,0.35)', color: '#a68eff' }}
                    >
                      <Columns size={13} /> Compare Sessions
                    </button>
                  )}
                  {/* Open synthesis in Content Studio */}
                  <button
                    onClick={() => {
                      localStorage.setItem('studio_topic', idea);
                      localStorage.setItem('studio_content', finalSynthesis || '');
                      window.location.href = '/content-studio';
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:scale-105"
                    style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)', color: '#f97316' }}
                  >
                    <Globe size={13} /> Push to Content Studio
                  </button>
                </div>

                {/* Continuation rounds */}
                <div className="rounded-xl p-4" style={{ background: 'rgba(124,94,250,0.06)', border: '1px solid rgba(124,94,250,0.2)' }}>
                  <div className="text-[10px] font-bold uppercase tracking-wider mb-2.5" style={{ color: 'rgba(166,142,255,0.7)' }}>
                    Deepen the Brainstorm — Run Another Council Round
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: 'Round 2: Challenge Assumptions', prompt: 'Challenge the assumptions in this output. What could go wrong? What are the weaknesses?\n\nPrevious synthesis:\n', color: '#f97316' },
                      { label: 'Round 3: Expand Ideas', prompt: 'Expand on the strongest ideas. What are 5 specific execution steps and success metrics?\n\nPrevious synthesis:\n', color: '#60a5fa' },
                      { label: 'Round 4: Final Blueprint', prompt: 'Create a concrete 90-day action plan based on this. Include milestones, resources needed, and KPIs.\n\nPrevious synthesis:\n', color: '#3dffa0' },
                    ].map((round, i) => (
                      <button
                        key={i}
                        data-testid={`continue-round-${i + 2}-btn`}
                        onClick={() => {
                          const synthesis = finalSynthesis || rounds.filter(r => r.synthesis).map(r => r.synthesis).join('\n\n');
                          setIdea(`${round.prompt}${synthesis.slice(0, 800)}`);
                          setStatus('idle');
                          setRounds([]);
                          setFinalSynthesis('');
                          window.scrollTo(0, 0);
                        }}
                        className="flex flex-col items-start p-2.5 rounded-xl text-left transition-all hover:scale-105"
                        style={{ background: `${round.color}0f`, border: `1px solid ${round.color}30`, color: round.color }}
                      >
                        <span className="text-[9px] font-black uppercase mb-0.5" style={{ color: round.color + '99' }}>Deepen</span>
                        <span className="text-[11px] font-semibold leading-snug text-white">{round.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Compare Panel */}
      {showCompare && <ComparePanel sessions={sessionHistory} onClose={() => setShowCompare(false)} />}
    </div>
  );
}
